import React, {useState, useMemo} from 'react';
import {Form, Input, InputNumber, Select} from 'antd';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY, onOffList} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {ShoppingRoadEntity} from "./ShoppingRoadEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import CustomDateTimePicker from "../../../shared/components/date/CustomDateTimePicker";
import DateStepper from "../../../shared/components/date/DateStepper";
import toast from "react-hot-toast";
import {useQueryClient} from "@tanstack/react-query";

const {Option} = Select;

const ShoppingRoadEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const [rows, setRows] = useState(
        record && record.seq ? new ShoppingRoadEntity(record) : new ShoppingRoadEntity({})
    );

    const managerRows = modalOptions?.managerRows || [];
    const groupRows = modalOptions?.groupRows || [];

    const uniqueGroupIdxList = useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const row of groupRows) {
            if (!seen.has(row.groupIdx)) {
                seen.add(row.groupIdx);
                result.push({groupIdx: row.groupIdx, memo: row.memo || ''});
            }
        }
        return result.sort((a, b) => a.groupIdx - b.groupIdx);
    }, [groupRows]);

    const handleSetRows = (key, value) => {
        setRows((prevState) => new ShoppingRoadEntity({...prevState, [key]: value}));
    };

    const handleSave = (e) => {
        e.preventDefault();
        const entity = rows instanceof ShoppingRoadEntity ? rows : new ShoppingRoadEntity(rows);
        if (typeof entity.isInvalidate === 'function' && entity.isInvalidate()) return;

        return internalApi.post('events/shopping_road', {data: entity.toJson()}).then((res) => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['shoppingRoad']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            toast.error('저장 실패');
        });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl('events/shopping_road', {seq: rows.seq})).then((res) => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['shoppingRoad']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch((err) => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <div style={{display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                    <Form.Item label="Memo" style={{flex: 1, minWidth: 200}}>
                        <Input
                            value={rows.memo}
                            onChange={e => handleSetRows('memo', e.target.value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="활성/비활성" style={{width: 100}}>
                        <Select
                            value={rows.onOff}
                            onChange={value => handleSetRows('onOff', value)}
                        >
                            {onOffList.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="계정 레벨" style={{width: 120}}>
                        <InputNumber
                            min={1}
                            value={rows.openLevel}
                            onChange={value => handleSetRows('openLevel', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>
                <Form.Item label="기간">
                    <CustomDateTimePicker rows={rows} handleSetRows={handleSetRows} hasAdd={false}/>
                </Form.Item>
                <DateStepper setRows={setRows} entity={ShoppingRoadEntity}/>
                <div style={{display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                    <Form.Item label="매니저 번호" style={{width: 160}}>
                        <Select
                            value={rows.managerIdx}
                            onChange={value => {
                                const manager = managerRows.find(m => m.seq === value);
                                setRows(prev => new ShoppingRoadEntity({
                                    ...prev,
                                    managerIdx: value,
                                    passIdx: manager?.eventPass ?? prev.passIdx,
                                }));
                            }}
                            placeholder="매니저 선택"
                        >
                            {managerRows.map((m) => (
                                <Option key={m.seq} value={m.seq}>
                                    {m.seq}{m.memo ? `(${m.memo})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="그룹 번호" style={{width: 160}}>
                        <Select
                            value={rows.groupIdx}
                            onChange={value => handleSetRows('groupIdx', value)}
                            placeholder="그룹 선택"
                        >
                            {uniqueGroupIdxList.map((g) => (
                                <Option key={g.groupIdx} value={g.groupIdx}>
                                    {g.groupIdx}{g.memo ? `(${g.memo})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="패스 번호"
                        style={{width: 140}}
                        tooltip="매니저 선택 시 자동으로 설정됩니다"
                    >
                        <InputNumber
                            min={0}
                            value={rows.passIdx}
                            disabled
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>
                <div style={{marginTop: 16}}>
                    <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={rows.seq > 0 ? 'flex' : 'none'}
                    />
                </div>
            </Form>
        </div>
    );
};

export default ShoppingRoadEditModal;